/* eslint-disable no-console */
import { apiRequest } from "./api";
import { Lang } from "./i18n";
import { getLanguage } from "./init";
import { OrgStruct } from "./org-struct";
import { Store } from "./store";

export interface Stats {
    readingFromDb: number;
    readingFromDevice: number;
}

interface WatchParam {
    id: number;
    deviceId: number;
    readFrom: "db" | "device";
}

interface WatchRawData {
    networkId: number;
    slaveId: number;
    addr: number;
    kind: "holding" | "coil" | "discrete-input" | "input";
    registersNum: number;
    transform: (v: number[]) => string;
}

interface WatchItem {
    key: AbsoluteParamKey;
    updatePeriodMs: number;
    priority: "low" | "normal" | "high";
    source: { param: WatchParam } | { rawData: WatchRawData };
    nextReadOn: number;
    oneTimeHighPriority?: boolean;
    rereadOnError: boolean;
}

const CYCLE_PERIOD_MS = 1000;
const HIGH_PRIORITY_MAX_WAIT_PERIOD_MS = 10000;
const MEDIUM_PRIORITY_MAX_WAIT_PERIOD_MS = 10000;
const LOW_PRIORITY_MAX_WAIT_PERIOD_MS = 3000;
const READ_TOGETHER_THRESHOLD_MS = 100;

type StoreKey = `store:${string}`;
type ParamKey = `param:${string}`;
type AbsoluteParamKey = `${StoreKey}>${ParamKey}`;

export class MultiStoreParamsWatcher {
    public groupByDeviceIfMoreThan = 4;
    public maxRealtimeReadingsPerRequest = 10;
    public useParallelRealtimeReadings = false;
    private readonly watchByStoreKey = new Map<AbsoluteParamKey, WatchItem>();
    private queueIsProcessing = false;
    private readonly readCallbacks = new Map<AbsoluteParamKey, (value: string) => void>();
    private readonly stores: Record<StoreKey, Store<string>> = {};

    private onStatsChange?: () => void;
    private stats: Stats = {
        readingFromDb: 0,
        readingFromDevice: 0,
    };

    public async start(): Promise<never> {
        while (true) {
            const start = Date.now();
            await this.processQueue();
            const timeToNextRead = start + CYCLE_PERIOD_MS - Date.now();
            if (timeToNextRead <= 0) {
                continue;
            }
            await new Promise((resolve) => {
                setTimeout(resolve, timeToNextRead);
            });
        }
    }

    public addStore(storeKey: StoreKey, store: Store<string>): void {
        this.stores[storeKey] = store;
    }

    public id(storeKey: StoreKey, paramKey: ParamKey): number {
        const absKey: AbsoluteParamKey = `${storeKey}>${paramKey}`;
        const item = this.watchByStoreKey.get(absKey);
        if (!item) {
            throw new Error(`key ${absKey} is not being watched`);
        }
        const source = item.source;
        if ("param" in source) {
            return source.param.id;
        }
        throw new Error(`key ${absKey} is not param`);
    }

    public realtime(
        storeKey: StoreKey,
        paramKey: ParamKey,
        paramId: number,
        deviceId: number,
        updatePeriodMs = 5000,
        priority: "low" | "normal" | "high" = "normal",
    ): void {
        this.watchParam(storeKey, paramKey, paramId, deviceId, "device", {
            updatePeriodMs,
            priority,
        });
    }

    public fromDb(
        storeKey: StoreKey,
        paramKey: ParamKey,
        paramId: number,
        deviceId: number,
        updatePeriodMs = 5000,
    ): void {
        this.watchParam(storeKey, paramKey, paramId, deviceId, "db", {
            updatePeriodMs,
        });
    }

    public watchRawData({
        storeKey,
        paramKey,
        updatePeriodMs,
        priority = "normal",
        networkId,
        slaveId,
        addr,
        kind,
        registersNum,
        transform,
        rereadOnError,
    }: {
        storeKey: StoreKey;
        paramKey: ParamKey;
        updatePeriodMs: number;
        priority?: "low" | "normal" | "high";
        networkId: number;
        slaveId: number;
        addr: number;
        kind: "holding" | "coil" | "discrete-input" | "input";
        registersNum: number;
        transform: (v: number[]) => string;
        rereadOnError?: boolean;
    }): void {
        const key: AbsoluteParamKey = `${storeKey}>${paramKey}`;
        this.watchByStoreKey.set(key, {
            key,
            updatePeriodMs,
            priority,
            source: { rawData: { networkId, slaveId, addr, kind, registersNum, transform } },
            nextReadOn: Date.now(),
            rereadOnError: rereadOnError ?? false,
        });
    }

    public async readNextCycle(storeKey: StoreKey, paramKey: ParamKey): Promise<string> {
        const key: AbsoluteParamKey = `${storeKey}>${paramKey}`;
        const item = this.watchByStoreKey.get(key);
        if (!item) {
            throw new Error(`key ${key} is not being watched`);
        }
        item.nextReadOn = Date.now();
        return new Promise((resolve) => {
            this.readCallbacks.set(key, resolve);
        });
    }

    public async readNowFromDevice(storeKey: StoreKey, paramKey: ParamKey): Promise<string> {
        const key: AbsoluteParamKey = `${storeKey}>${paramKey}`;
        const item = this.watchByStoreKey.get(key);
        if (!item) {
            throw new Error(`key ${key} is not being watched`);
        }
        return (await this.read([item], "device"))[0];
    }

    public async readManyNowFromDevice(
        storeKey: StoreKey,
        paramKeys: ParamKey[],
    ): Promise<string[]> {
        const keys = paramKeys.map<AbsoluteParamKey>((key) => `${storeKey}>${key}`);
        const items = keys.map((key) => {
            const item = this.watchByStoreKey.get(key);
            if (!item) {
                throw new Error(`key ${key} is not being watched`);
            }
            return item;
        });
        return await this.read(items, "device");
    }

    public subscribeStats = (onStatsChange: () => void): (() => void) => {
        this.onStatsChange = onStatsChange;
        return () => {
            this.onStatsChange = undefined;
        };
    };

    public getStats = (): Stats => {
        return this.stats;
    };

    public async writeParam(
        storeKey: StoreKey,
        paramKey: ParamKey,
        value: string,
        throwOnError = true,
        rereadAfterWrite = true,
        lang?: Lang,
    ): Promise<void> {
        const id = this.id(storeKey, paramKey);
        await writeParam(id, value, throwOnError ? "throw" : "alert", lang);
        if (rereadAfterWrite) {
            await this.readNowFromDevice(storeKey, paramKey);
        }
    }

    public async rawWriteRegister(
        storeKey: StoreKey,
        paramKey: ParamKey,
        value: number[],
        throwOnError = true,
        rereadAfterWrite = true,
    ): Promise<void> {
        const key: AbsoluteParamKey = `${storeKey}>${paramKey}`;
        const item = this.watchByStoreKey.get(key);
        if (!item) {
            throw new Error(`storeKey ${storeKey} is not being watched`);
        }

        if (!("rawData" in item.source)) {
            throw new Error(`storeKey ${storeKey} is not raw register`);
        }

        await rawWriteRegister(
            item.source.rawData.networkId,
            item.source.rawData.slaveId,
            item.source.rawData.addr,
            item.source.rawData.kind,
            value,
            throwOnError ? "throw" : "alert",
        );

        if (rereadAfterWrite) {
            await this.readNowFromDevice(storeKey, paramKey);
        }
    }

    public getWatchingStoreKeys(): string[] {
        return Array.from(this.watchByStoreKey.keys());
    }

    public removeWatchedItems(storeKey: StoreKey, paramKeys: ParamKey[]): void {
        for (const paramKey of paramKeys) {
            const key: AbsoluteParamKey = `${storeKey}>${paramKey}`;
            this.watchByStoreKey.delete(key);
        }
    }

    private async processQueue(): Promise<void> {
        if (this.queueIsProcessing) {
            return;
        }
        this.queueIsProcessing = true;

        // find items that should be read in current cycle
        const start = Date.now();
        const readItems: WatchItem[] = [];
        for (const [_, item] of this.watchByStoreKey) {
            if (item.nextReadOn + READ_TOGETHER_THRESHOLD_MS > start) {
                continue;
            }
            readItems.push(item);
        }

        // group by priority
        const fromDb: WatchItem[] = [];
        const deviceHigh: WatchItem[] = [];
        const deviceNormal: WatchItem[] = [];
        const deviceLow: WatchItem[] = [];
        for (const item of readItems) {
            if ("param" in item.source && item.source.param.readFrom === "db") {
                fromDb.push(item);
            } else if (
                item.priority === "high" ||
                (item.priority === "normal" &&
                    start - item.nextReadOn > item.updatePeriodMs * 2) ||
                item.oneTimeHighPriority
            ) {
                deviceHigh.push(item);
            } else if (item.priority === "normal") {
                deviceNormal.push(item);
            } else {
                deviceLow.push(item);
            }
        }

        this.updateStats(() => ({
            readingFromDb: fromDb.length,
            readingFromDevice: deviceHigh.length + deviceNormal.length + deviceLow.length,
        }));

        // read items
        await this.read(fromDb, "db");
        if (Date.now() - start >= HIGH_PRIORITY_MAX_WAIT_PERIOD_MS) {
            console.error(`Reading from database took ${Date.now() - start}ms!`);
            console.warn(`Aborting reading all parameter from devices.`);
            this.queueIsProcessing = false;
            return;
        }
        await this.read(deviceHigh, "device");
        if (Date.now() - start >= MEDIUM_PRIORITY_MAX_WAIT_PERIOD_MS) {
            console.warn(
                `Reading high priority parameters took ${Date.now() - start}ms. ` +
                    `Aborting reading other parameters.`,
            );
            this.queueIsProcessing = false;
            return;
        }
        await this.read(deviceNormal, "device");
        if (Date.now() - start >= LOW_PRIORITY_MAX_WAIT_PERIOD_MS) {
            console.warn(
                `Reading high and medium priority parameters took ${Date.now() - start}ms. ` +
                    `Aborting reading low priority parameters.`,
            );
            this.queueIsProcessing = false;
            return;
        }
        await this.read(deviceLow, "device");
        this.queueIsProcessing = false;
    }

    private watchParam(
        storeKey: StoreKey,
        paramKey: ParamKey,
        paramId: number,
        deviceId: number,
        readFrom: "db" | "device" = "db",
        opts?: {
            updatePeriodMs?: number;
            priority?: "low" | "normal" | "high";
            rereadOnError?: boolean;
        },
    ): void {
        const key: AbsoluteParamKey = `${storeKey}>${paramKey}`;
        this.watchByStoreKey.set(key, {
            key,
            updatePeriodMs: opts?.updatePeriodMs ?? 5000,
            priority: opts?.priority ?? "normal",
            source: { param: { id: paramId, deviceId, readFrom } },
            nextReadOn: Date.now(),
            rereadOnError: opts?.rereadOnError ?? false,
        });
    }

    private processReadResults(
        watchItems: WatchItem[],
        results: Map<AbsoluteParamKey, string>,
    ): void {
        // save results to store
        for (const storeKeyStr in this.stores) {
            const storeKey = storeKeyStr as StoreKey;
            const store = this.stores[storeKey];
            store.setMany(
                Object.fromEntries(
                    Array.from(results.entries())
                        .filter((item) => item[0].split(">")[0] === storeKeyStr)
                        .map((item) => [item[0].replace(`${storeKeyStr}>param:`, ""), item[1]]),
                ),
            );
        }

        // update readOn time
        for (const item of watchItems) {
            if (item.rereadOnError) {
                if (results.get(item.key) === "NORT") {
                    item.nextReadOn = Date.now();
                    continue;
                }
            }
            item.nextReadOn = Date.now() + item.updatePeriodMs;
            if (item.oneTimeHighPriority) {
                item.oneTimeHighPriority = false;
            }
            if (this.readCallbacks.has(item.key)) {
                const callback = this.readCallbacks.get(item.key);
                this.readCallbacks.delete(item.key);
                callback?.(results.get(item.key) || "ERR");
            }
        }
    }

    private async read(watchItems: WatchItem[], readFrom: "db" | "device"): Promise<string[]> {
        if (watchItems.length === 0) {
            return [];
        }
        const results = new Map<AbsoluteParamKey, string>();

        // if requests are from db, read them right away
        if (readFrom === "db") {
            await this.readFromDb(watchItems, results);
        } else {
            const paramRequests = watchItems.filter((i) => "param" in i.source);
            await this.readParamsFromDevices(paramRequests, results);
            const rawRequests = watchItems.filter((i) => "rawData" in i.source);
            await this.readRawRequests(rawRequests, results);
        }

        return Array.from(results.values());
    }

    private async readFromDb(
        items: WatchItem[],
        results: Map<AbsoluteParamKey, string>,
    ): Promise<void> {
        if (items.length === 0) {
            return;
        }

        const paramItems = items as (WatchItem & { source: { param: WatchParam } })[];

        let resp: Record<string, string>;
        try {
            resp = (await readParamsFromDb(paramItems.map((i) => i.source.param.id))).data;
        } catch (e) {
            console.error(`API error: ${(e as Error).message}`);
            for (const item of paramItems) {
                results.set(item.key, "ERR");
            }
            this.updateStats((stats) => ({ ...stats, readingFromDb: 0 }));
            this.processReadResults(items, results);
            return;
        }

        for (const item of paramItems) {
            results.set(item.key, resp[item.source.param.id]);
        }
        this.processReadResults(items, results);
        this.updateStats((stats) => ({ ...stats, readingFromDb: 0 }));
    }

    private async readParamsFromDevices(
        watchItems: WatchItem[],
        results: Map<AbsoluteParamKey, string>,
    ): Promise<void> {
        if (watchItems.length === 0) {
            return;
        }
        type Item = WatchItem & { source: { param: WatchParam } };
        const items = watchItems as Item[];

        // if there are many params from one device group them
        const groupedByDeviceId = new Map<number, Item[]>();
        for (const item of items) {
            const deviceId = item.source.param.deviceId;
            const existingGroup = groupedByDeviceId.get(deviceId);
            if (existingGroup) {
                existingGroup.push(item);
            } else {
                groupedByDeviceId.set(deviceId, [item]);
            }
        }
        const requestGroups: Item[][] = [];
        for (const [key, group] of groupedByDeviceId.entries()) {
            if (group.length > this.groupByDeviceIfMoreThan) {
                requestGroups.push(group);
                groupedByDeviceId.delete(key);
            }
        }
        const leftover = Array.from(groupedByDeviceId.values()).flat();
        if (leftover.length > 0) {
            for (let i = 0; i < leftover.length; i += this.maxRealtimeReadingsPerRequest) {
                const chunk = leftover.slice(i, i + this.maxRealtimeReadingsPerRequest);
                requestGroups.push(chunk);
            }
        }

        // send requests to devices
        const promises: Promise<void>[] = [];
        for (const group of requestGroups) {
            promises.push(this.readParamsGroupFromDevices(group, results));
        }
        if (this.useParallelRealtimeReadings) {
            await Promise.all(promises);
        } else {
            for (const p of promises) {
                await p;
            }
        }
    }

    private async readParamsGroupFromDevices(
        group: (WatchItem & { source: { param: WatchParam } })[],
        results: Map<AbsoluteParamKey, string>,
    ): Promise<void> {
        const query = new URLSearchParams({
            paramIds: group.map((i) => i.source.param.id.toString()).join(","),
            lang: getLanguage(),
        }).toString();
        const url = `/v1/params/read-realtime/?${query}`;

        let resp: ParamsGetRealtimeReadingsResponse;
        try {
            resp = await apiRequest(url, "get");
        } catch (e) {
            console.error(`API error: ${(e as Error).message}`);
            for (const item of group) {
                results.set(item.key, "ERR");
            }
            this.processReadResults(group, results);
            this.updateStats((stats) => ({
                ...stats,
                readingFromDevice: stats.readingFromDevice - group.length,
            }));
            return;
        }

        for (const item of group) {
            const reading = resp[item.source.param.id];
            if (!reading || "err" in reading) {
                results.set(item.key, "NORT");
            } else {
                let value = reading.formattedValue;
                if (Number.isNaN(Number(value))) {
                    value = reading.value;
                }
                results.set(item.key, value);
            }
        }
        this.processReadResults(group, results);
        this.updateStats((stats) => ({
            ...stats,
            readingFromDevice: stats.readingFromDevice - group.length,
        }));
    }

    private async readRawRequests(
        items: WatchItem[],
        results: Map<AbsoluteParamKey, string>,
    ): Promise<void> {
        if (items.length === 0) {
            return;
        }
        type Item = WatchItem & { source: { rawData: WatchRawData } };
        const rawItems = items as Item[];

        // group requests by networks
        const groupedByNetId = new Map<number, Item[]>();
        for (const item of rawItems) {
            const netId = item.source.rawData.networkId;
            const existingGroup = groupedByNetId.get(netId);
            if (existingGroup) {
                existingGroup.push(item);
            } else {
                groupedByNetId.set(netId, [item]);
            }
        }

        const reqData: {
            deviceModbusId: number;
            address: number;
            registersNum: number;
            kind: "holding" | "coil" | "discrete-input" | "input";
            connection: {
                kind: string;
                networkId: number;
            };
        }[] = [];

        // send requests to devices
        for (const [netId, group] of groupedByNetId.entries()) {
            // sort by address
            group.sort((a, b) => a.source.rawData.addr - b.source.rawData.addr);

            for (const [i, item] of group.entries()) {
                if (
                    i > 0 &&
                    item.source.rawData.addr === group[i - 1].source.rawData.addr + 1 &&
                    item.source.rawData.kind === group[i - 1].source.rawData.kind
                ) {
                    reqData[reqData.length - 1].registersNum += item.source.rawData.registersNum;
                    continue;
                }
                reqData.push({
                    deviceModbusId: item.source.rawData.slaveId,
                    address: item.source.rawData.addr,
                    registersNum: item.source.rawData.registersNum,
                    kind: item.source.rawData.kind,
                    connection: { kind: "net-id", networkId: netId },
                });
            }
        }

        let resp: RawReadMultipleRegisterGroupResponse = [];
        try {
            resp = await apiRequest(`/v1/raw/read-multiple-register-group/`, "post", {
                body: JSON.stringify(reqData),
            });
        } catch (e) {
            console.error(`API error: ${(e as Error).message}`);
        }

        for (const [_netId, group] of groupedByNetId.entries()) {
            let sameRespCount = 0;
            for (const [i, item] of group.entries()) {
                sameRespCount =
                    i > 0 &&
                    item.source.rawData.addr === group[i - 1].source.rawData.addr + 1 &&
                    item.source.rawData.kind === group[i - 1].source.rawData.kind
                        ? sameRespCount + group[i - 1].source.rawData.registersNum
                        : 0;

                const address = item.source.rawData.addr - sameRespCount;
                const r = resp.find(
                    (i) =>
                        i.deviceModbusId === item.source.rawData.slaveId &&
                        i.address === address &&
                        i.kind === item.source.rawData.kind,
                );

                if (!r || "err" in r.result) {
                    results.set(item.key, "NORT");
                    continue;
                }

                const value = r.result.ok.splice(0, item.source.rawData.registersNum);
                const formattedValue = item.source.rawData.transform(value);
                results.set(item.key, formattedValue);
            }

            this.processReadResults(items, results);
            this.updateStats((stats) => ({
                ...stats,
                readingFromDevice: stats.readingFromDevice - group.length,
            }));
        }
    }

    private updateStats(updater: (stats: Stats) => Stats): void {
        this.stats = updater(this.stats);
        this.onStatsChange?.();
    }
}

type ParamsGetLastReadingsResponse = Record<
    string,
    | {
          value: string;
          readOn: string;
          formattedValue: string;
      }
    | {
          err: string;
      }
>;

type ParamsGetRealtimeReadingsResponse = Record<
    string,
    | {
          value: string;
          formattedValue: string;
      }
    | {
          err: string;
      }
>;

type RawReadMultipleRegisterGroupResponse = {
    netLid: string;
    deviceModbusId: number;
    address: number;
    kind: "holding" | "coil" | "discrete-input" | "input";
    registersNum: number;
    result:
        | {
              ok: number[];
          }
        | {
              err: string;
          };
}[];

export type ParamWriteResponse =
    | {
          ok: {
              oldValue?: string;
              newValue: string;
              formattedValue: string;
          };
      }
    | {
          err: string;
      };

export async function writeParam(
    id: number,
    value: string,
    onError: "alert" | "throw" | "return" = "throw",
    lang?: Lang,
): Promise<ParamWriteResponse> {
    if (!lang) {
        lang = getLanguage();
    }
    let res: ParamWriteResponse;
    try {
        res = await apiRequest(`/v1/param/${id}/write/`, "post", {
            body: JSON.stringify({ newValue: value, lang }),
        });
    } catch (e) {
        console.error(`API error: ${(e as Error).message}`);
        if (onError === "throw") {
            throw e;
        } else if (onError === "alert") {
            alert(`API error: ${(e as Error).message}`);
            return { err: (e as Error).message };
        } else {
            return { err: (e as Error).message };
        }
    }
    if ("err" in res) {
        if (onError === "throw") {
            throw new Error(res.err);
        } else if (onError === "alert") {
            // TODO_FUTURE: proper message and translation
            alert(res.err);
        }
    }
    return res;
}

type RawWriteRegisterResponse =
    | {
          status: "ok";
      }
    | {
          status: "error";
          errorMessage: string;
      };

export async function rawWriteRegister(
    netId: number,
    deviceModbusId: number,
    address: number,
    kind: "holding" | "coil" | "discrete-input" | "input",
    value: number[],
    onError: "alert" | "throw" | "return" = "throw",
): Promise<RawWriteRegisterResponse> {
    let res: RawWriteRegisterResponse;

    try {
        res = await apiRequest(`/v1/raw/write-register-group/`, "post", {
            body: JSON.stringify({
                deviceModbusId,
                address,
                kind,
                value,
                connection: {
                    kind: "net-id",
                    networkId: netId,
                },
            }),
        });
    } catch (e) {
        console.error(`API error: ${(e as Error).message}`);
        if (onError === "throw") {
            throw e;
        } else if (onError === "alert") {
            alert(`API error: ${(e as Error).message}`);
            return {
                status: "error",
                errorMessage: (e as Error).message,
            };
        } else {
            return {
                status: "error",
                errorMessage: (e as Error).message,
            };
        }
    }

    if ("errorMessage" in res) {
        if (onError === "throw") {
            throw new Error(res.errorMessage);
        } else if (onError === "alert") {
            // TODO_FUTURE: proper message and translation
            alert(res.errorMessage);
        }
    }

    return res;
}

export async function getLastReadings(
    storeKeys: string[],
    lang: Lang,
    getParamId: (key: string) => number,
): Promise<{ data: Record<string, string>; lastReadOn: Date }> {
    const paramIdKeyMap = new Map<number, string>();
    for (const key of storeKeys) {
        paramIdKeyMap.set(getParamId(key), key);
    }

    const resp = await readParamsFromDb(Array.from(paramIdKeyMap.keys()));

    const data: Record<string, string> = {};
    for (const [id, key] of paramIdKeyMap) {
        data[key] = resp.data[id];
    }

    return { data, lastReadOn: resp.lastReadOn };
}

async function readParamsFromDb(
    paramIds: number[],
): Promise<{ data: Record<string, string>; lastReadOn: Date }> {
    const query = new URLSearchParams({
        paramIds: paramIds.join(","),
        lang: getLanguage(),
    }).toString();
    const url = `/v1/params/last-readings/?${query}`;
    let resp: ParamsGetLastReadingsResponse;
    try {
        resp = await apiRequest(url, "get");
    } catch (e) {
        console.error(`API error: ${(e as Error).message}`);
        return {
            data: Object.fromEntries(paramIds.map((id) => [id.toString(), "ERR"])),
            lastReadOn: new Date(),
        };
    }

    const data: Record<string, string> = {};
    for (const [id, reading] of Object.entries(resp)) {
        if ("err" in reading) {
            if (reading.err === "no-value") {
                data[id] = "NOLV";
            } else {
                data[id] = "ERR";
            }
        } else {
            data[id] = reading.formattedValue;
        }
    }

    const lastReadOn = new Date(
        Math.max(
            ...Object.values(resp).map((r) => ("err" in r ? 0 : new Date(r.readOn).getTime())),
        ),
    );

    return {
        data,
        lastReadOn,
    };
}

export class ParamsWatcher {
    private readonly storeKey: StoreKey;

    constructor(
        visId: string,
        store: Store<string>,
        public readonly watcher: MultiStoreParamsWatcher,
        private readonly orgStruct: OrgStruct,
    ) {
        this.storeKey = `store:${visId}`;
        this.watcher.addStore(this.storeKey, store);
    }

    public getStoreKey(): StoreKey {
        return this.storeKey;
    }

    public id(paramKeyStr: string): number {
        const paramKey: ParamKey = `param:${paramKeyStr}`;
        return this.watcher.id(this.storeKey, paramKey);
    }

    public realtime(
        paramKeyStr: string,
        paramPath: string,
        updatePeriodMs = 5000,
        priority: "low" | "normal" | "high" = "normal",
    ): void {
        const paramId = this.orgStruct.id(paramPath);
        const deviceId = this.orgStruct.params[paramId].deviceId;
        const paramKey: ParamKey = `param:${paramKeyStr}`;
        this.watcher.realtime(
            this.storeKey,
            paramKey,
            paramId,
            deviceId,
            updatePeriodMs,
            priority,
        );
    }

    public watchRawData({
        paramKeyStr,
        updatePeriodMs,
        priority = "normal",
        networkId,
        slaveId,
        addr,
        kind,
        registersNum,
        transform,
        rereadOnError,
    }: {
        paramKeyStr: string;
        updatePeriodMs: number;
        priority?: "low" | "normal" | "high";
        networkId: number;
        slaveId: number;
        addr: number;
        kind: "holding" | "coil" | "discrete-input" | "input";
        registersNum: number;
        transform: (v: number[]) => string;
        rereadOnError?: boolean;
    }): void {
        const paramKey: ParamKey = `param:${paramKeyStr}`;
        this.watcher.watchRawData({
            storeKey: this.storeKey,
            paramKey,
            updatePeriodMs,
            priority,
            networkId,
            slaveId,
            addr,
            kind,
            registersNum,
            transform,
            rereadOnError,
        });
    }

    public async writeParam(
        paramKeyStr: string,
        value: string,
        throwOnError = true,
        rereadAfterWrite = true,
        lang?: Lang,
    ): Promise<void> {
        const paramKey: ParamKey = `param:${paramKeyStr}`;
        await this.watcher.writeParam(
            this.storeKey,
            paramKey,
            value,
            throwOnError,
            rereadAfterWrite,
            lang,
        );
    }

    public async rawWriteRegister(
        paramKeyStr: string,
        value: number[],
        throwOnError = true,
        rereadAfterWrite = true,
    ): Promise<void> {
        const paramKey: ParamKey = `param:${paramKeyStr}`;
        await this.watcher.rawWriteRegister(
            this.storeKey,
            paramKey,
            value,
            throwOnError,
            rereadAfterWrite,
        );
    }

    public fromDb(paramKeyStr: string, param: string, updatePeriodMs = 5000): void {
        const paramId = this.orgStruct.id(param);
        const deviceId = this.orgStruct.params[paramId].deviceId;
        const paramKey: ParamKey = `param:${paramKeyStr}`;
        this.watcher.fromDb(this.storeKey, paramKey, paramId, deviceId, updatePeriodMs);
    }

    public async readNowFromDevice(paramKeyStr: string): Promise<string> {
        const paramKey: ParamKey = `param:${paramKeyStr}`;
        return await this.watcher.readNowFromDevice(this.storeKey, paramKey);
    }

    public async readManyNowFromDevice(paramKeyStrs: string[]): Promise<string[]> {
        const paramKeys = paramKeyStrs.map<ParamKey>((key) => `param:${key}`);
        return await this.watcher.readManyNowFromDevice(this.storeKey, paramKeys);
    }

    public async readNextCycle(paramKeyStr: string): Promise<string> {
        const paramKey: ParamKey = `param:${paramKeyStr}`;
        return await this.watcher.readNextCycle(this.storeKey, paramKey);
    }

    public removeWatchedItems(keys: string[]): void {
        this.watcher.removeWatchedItems(
            this.storeKey,
            keys.map((k) => {
                const paramKey: ParamKey = `param:${k}`;
                return paramKey;
            }),
        );
    }

    public subscribeStats(onStatsChange: () => void): () => void {
        return this.watcher.subscribeStats(onStatsChange);
    }

    public getStats(): Stats {
        return this.watcher.getStats();
    }

    public getWatchingStoreKeys(): string[] {
        return this.watcher
            .getWatchingStoreKeys()
            .filter((key) => key.startsWith(`${this.storeKey}>`))
            .map((key) => key.replace(`${this.storeKey}>`, ""));
    }

    public start(): Promise<never> {
        return this.watcher.start();
    }
}
